import { Button, ButtonTheme, BUTTON_HEIGHT } from 'components/Button';
import { ICONS } from 'components/Icon';
import { Menu } from 'components/Menu';
import { ModalScreens } from 'components/Navigation/types';
import { ViewWithSeparators } from 'components/ViewWithSeparators';
import { doAddEvent, doRemoveEvents } from 'lib/events';
import { pureComponent } from 'lib/pureComponent';
import { randomKey } from 'lib/riskbase';
import { smartText } from 'lib/str';
import { theme } from 'lib/theme';
import React from 'react';
import { Platform, StyleSheet, View } from 'react-native';
import { useDispatch } from 'react-redux';
import { FileTypeGroupsEnum } from 'service/file/types';
import {
  doAppFileGetFilesFromPicker,
  doAppFileGetImagesFromPicker,
  eventFilesAdded,
  doSetupOrganisationSaveLogo,
  ALERT,
  NAVIGATE_MODAL,
} from 'store/actions';

export const OrganisationLogoButtons: React.FC = pureComponent(() => {
  const dispatch = useDispatch();
  const eventKey = React.useRef(randomKey());

  React.useEffect(() => {
    const events = [
      doAddEvent(eventFilesAdded, (event) => {
        if (event.payload.key === eventKey.current) {
          const file = event.payload.files[0];
          if (file) {
            dispatch(
              doSetupOrganisationSaveLogo({
                file,
              }),
            );
          }
        }
      }),
    ];
    return () => {
      doRemoveEvents(events);
    };
  }, [dispatch]);

  const buttonChildren = [];
  const otherButtonsConfig: {
    key: string;
    text: string;
    icon: ICONS;
    onPress: () => void;
    disabled?: boolean;
    destructive?: boolean;
  }[] = [];

  buttonChildren.push(
    <Button
      key="image"
      buttonTheme={ButtonTheme.themeless}
      icon={ICONS.ImageLight}
      onPress={() =>
        dispatch(
          doAppFileGetImagesFromPicker({
            eventKey: eventKey.current,
            multiSelect: false,
            returnType: 'event',
          }),
        )
      }
      style={styles.button}
    />,
  );

  if (Platform.OS !== 'web') {
    buttonChildren.push(
      <Button
        key="file"
        buttonTheme={ButtonTheme.themeless}
        icon={ICONS.FolderOpenLight}
        onPress={() =>
          dispatch(
            doAppFileGetFilesFromPicker({
              eventKey: eventKey.current,
              multiSelect: false,
              allowedFileGroups: [FileTypeGroupsEnum.image],
              returnType: 'event',
            }),
          )
        }
        style={styles.button}
      />,
    );
  }

  otherButtonsConfig.push({
    key: 'info',
    text: 'Logo Information',
    icon: ICONS.CircleInfoLight,
    onPress: () =>
      dispatch(
        ALERT({
          title: 'Organisation Logo',
          text: smartText(
            'Your logo will look best on reports uploaded as an SVG. ' +
              'This file type can be scaled up and down without losing quality. ' +
              'You may have been provided with an SVG by the logo designer.\n\n' +
              "If you don't have an SVG then a normal PNG image or JPEG image can be uploaded.\n\n" +
              'For help, get in touch with RiskBase Support',
          ),
          altButton: {
            text: 'Contact Support',
            closeAlert: true,
            action: NAVIGATE_MODAL({
              screen: ModalScreens.Support,
              params: {},
            }),
          },
        }),
      ),
  });

  otherButtonsConfig.push({
    key: 'remove',
    text: 'Remove Logo',
    icon: ICONS.TrashCanLight,
    destructive: true,
    onPress: () =>
      dispatch(
        doSetupOrganisationSaveLogo({
          file: null,
        }),
      ),
  });

  if (otherButtonsConfig.length) {
    buttonChildren.push(
      <Menu
        key="other"
        buttonConfig={{
          buttonTheme: ButtonTheme.themeless,
          style: styles.button,
          icon: ICONS.EllipsisLight,
        }}
        width={200}
        options={otherButtonsConfig}
      />,
    );
  }

  if (!buttonChildren.length) {
    return null;
  }

  return (
    <View style={[styles.buttons, styles.buttonsShadow]}>
      <ViewWithSeparators
        horizontal
        separator={<View style={styles.divider} />}
      >
        {buttonChildren}
      </ViewWithSeparators>
    </View>
  );
});

const styles = StyleSheet.create({
  button: {
    borderRadius: 0,
    height: BUTTON_HEIGHT - 2,
    paddingLeft: theme.paddingHorizontal - 2,
    paddingRight: theme.paddingHorizontal - 2,
  },
  buttons: {
    backgroundColor: 'white',
    borderColor: theme.colors.blue80,
    borderRadius: 4,
    borderWidth: 1,
    flexDirection: 'row',
    justifyContent: 'center',
    height: BUTTON_HEIGHT,
  },
  buttonsShadow: {
    shadowColor: theme.colors.blue,
    shadowOffset: { height: 0, width: 0 },
    shadowOpacity: 0.2,
    shadowRadius: 2,
  },
  container: {
    backgroundColor: 'white',
    paddingHorizontal: theme.paddingHorizontal,
    paddingVertical: theme.grid,
  },
  divider: {
    borderRightColor: theme.colors.blue80,
    borderRightWidth: 1,
    height: BUTTON_HEIGHT - 2,
  },
  imageGridContainer: {
    flex: 1,
    flexDirection: 'row',
    flexWrap: 'wrap',
    paddingBottom: theme.grid,
  },
  label: {
    alignItems: 'center',
    backgroundColor: theme.colors.blue80,
    justifyContent: 'center',
    minWidth: theme.grid * 2,
  },
});
